import { useEffect, useState, useCallback, useMemo } from "react";
import { apiGet, apiPost, apiDelete, apiPatch } from "../api/http";
import { parseApiError } from "../utils/apiErrors";
import SimpleUrlInput from "../components/SimpleUrlInput";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import { useActiveSite } from "../context/ActiveSiteContext";
import { isValidWebsiteUrl } from "../utils/urlValidation";
import { isValidEmail, normalizeEmail } from "../utils/email";
import DataTable from "../components/shared/DataTable";
import EditScheduleModal from "../components/EditScheduleModal";
import PlanGateBanner from "../components/shared/PlanGateBanner";

const FREQUENCIES = [
  { value: "daily",   label: "Daily",   desc: "Every 24 hours" },
  { value: "weekly",  label: "Weekly",  desc: "Every 7 days" },
  { value: "monthly", label: "Monthly", desc: "Every 30 days" },
];
function freqLabel(f) {
  return FREQUENCIES.find((x) => x.value === f)?.label || f;
}

function scoreColor(score) {
  if (score == null) return "var(--text-muted)";
  if (score >= 80) return "var(--success)";
  if (score >= 60) return "#84cc16";
  if (score >= 40) return "var(--warning)";
  return "var(--danger)";
}

function nextRunLabel(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = d - now;
  if (diffMs < 0) return "Due now";
  const diffH = Math.floor(diffMs / 3600000);
  if (diffH < 1) return "< 1 hour";
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD}d`;
}

export default function Schedules() {
  const { user } = useAuth();
  const { showSuccess, showError, showInfo } = useToast();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const { activeSite } = useActiveSite();

  useEffect(() => {
    if (activeSite) {
      setUrl(activeSite.url);
    }
  }, [activeSite]);

  const urlMatchesActiveSite = (url, activeSite) => {
    if (!activeSite || !url) return false;
    try {
      const activeHost = new URL(activeSite.url).hostname.replace(/^www\./i, "").toLowerCase();
      const targetHost = new URL(url).hostname.replace(/^www\./i, "").toLowerCase();
      return activeHost === targetHost || url.toLowerCase().includes(activeHost);
    } catch {
      const activeClean = activeSite.url.replace(/^https?:\/\//i, "").replace(/^www\./i, "").toLowerCase();
      const urlClean = url.replace(/^https?:\/\//i, "").replace(/^www\./i, "").toLowerCase();
      return urlClean.includes(activeClean) || activeClean.includes(urlClean);
    }
  };

  const filteredSchedules = useMemo(() => {
    if (isAdmin) return schedules;
    if (!activeSite) return [];
    return schedules.filter((s) => urlMatchesActiveSite(s.url, activeSite));
  }, [schedules, activeSite, isAdmin]);

  // Form state
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [frequency, setFrequency] = useState("weekly");
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [slackWebhookUrl, setSlackWebhookUrl] = useState("");
  const [adminEmailFilter, setAdminEmailFilter] = useState("");
  const [adminUserIdFilter, setAdminUserIdFilter] = useState("");
  const [adminStatusFilter, setAdminStatusFilter] = useState("");
  const [appliedAdminFilters, setAppliedAdminFilters] = useState({
    email: "",
    userid: "",
    status: "",
  });
  const [adminPage, setAdminPage] = useState(1);
  const [adminPagination, setAdminPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 20 });
  
  const planName = String(user?.plan_name || "free").toLowerCase();
  const hasPremiumPlan = ["solo", "agency", "premium"].includes(planName);
  
  let maxSchedules = Number(user?.limits?.max_schedules ?? 0);
  if (maxSchedules === 0 && hasPremiumPlan) {
    if (planName === "solo") maxSchedules = 10;
    else if (planName === "agency") maxSchedules = 30;
    else maxSchedules = 999;
  }
  
  const isLimitReached = !isAdmin && schedules.length >= maxSchedules;
  const isPremiumOnly = !isAdmin && maxSchedules === 0 && !hasPremiumPlan;

  const load = useCallback(async () => {
    if (isPremiumOnly && !isAdmin) {
      setSchedules([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = isAdmin
        ? {
            email: appliedAdminFilters.email || undefined,
            userid: appliedAdminFilters.userid || undefined,
            status: appliedAdminFilters.status || undefined,
            page: adminPage,
            limit: adminPagination.limit,
          }
        : {};
      const { data } = await apiGet("/api/schedules", params);
      if (isAdmin) {
        const rows = Array.isArray(data?.data) ? data.data : [];
        const pagination = data?.pagination || {
          page: adminPage,
          totalPages: 1,
          total: rows.length,
          limit: adminPagination.limit,
        };
        setSchedules(rows);
        setAdminPagination(pagination);
        setAdminPage(pagination.page || 1);
      } else {
        setSchedules(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      showError(parseApiError(err, "Failed to load schedules"));
    } finally {
      setLoading(false);
    }
  }, [
    adminPage,
    adminPagination.limit,
    appliedAdminFilters,
    isAdmin,
    isPremiumOnly,
    showError,
  ]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (isLimitReached) {
      showError("Schedule limit reached. Please upgrade to add more.");
      return;
    }
    if (!url.trim() || !frequency) return;
    if (!isValidWebsiteUrl(url.trim())) {
      showError("Please enter a valid URL.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await apiPost("/api/schedules", {
        url: url.trim(),
        label: label.trim(),
        frequency,
        notifyEmail,
        webhookUrl: webhookUrl.trim() || null,
        slackWebhookUrl: slackWebhookUrl.trim() || null,
      });
      setSchedules((prev) => {
        const filtered = prev.filter((s) => s.id !== data.id);
        return [data, ...filtered];
      });
      setUrl("");
      setLabel("");
      setFrequency("weekly");
      setNotifyEmail(true);
      setWebhookUrl("");
      setSlackWebhookUrl("");
      if (isAdmin) {
        load();
      }
      showSuccess("Schedule created.");
    } catch (err) {
      showError(parseApiError(err, "Failed to create schedule"));
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (schedule) => {
    const previous = schedules;
    const optimistic = previous.map((s) =>
      s.id === schedule.id ? { ...s, enabled: !s.enabled } : s
    );
    setSchedules(optimistic);
    try {
      await apiPatch(`/api/schedules/${schedule.id}`, { enabled: !schedule.enabled });
      showInfo(`Schedule ${!schedule.enabled ? "enabled" : "paused"}.`);
    } catch {
      setSchedules(previous);
      showError("Could not update schedule status.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this schedule?")) return;
    setSchedules((prev) => prev.filter((s) => s.id !== id));
    try {
      await apiDelete(`/api/schedules/${id}`);
      showSuccess("Schedule removed.");
    } catch (err) {
      showError(parseApiError(err, "Failed to delete schedule"));
      load();
    }
  };

  const applyAdminFilters = () => {
    const trimmedEmail = normalizeEmail(adminEmailFilter);
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      showError("Please enter a valid email for filtering.");
      return;
    }
    const nextFilters = {
      email: trimmedEmail,
      userid: adminUserIdFilter.trim(),
      status: adminStatusFilter,
    };
    setAdminPage(1);
    setAppliedAdminFilters(nextFilters);
  };

  return (
    <div className={`page ${isAdmin ? "page-admin-wide" : ""}`.trim()}>
      <h1 className="page-title">Scheduled Monitoring</h1>
      <p className="dashboard-lead" style={{ marginTop: "-8px" }}>
        Set recurring SEO audits for any URL. Results are saved to your audit history automatically.
      </p>

      {/* Create form */}
      <div className="card" style={{
        marginBottom: 24,
        position: "relative",
        overflow: "hidden",
        minHeight: isPremiumOnly ? "260px" : "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
      }}>
        <h2 className="page-subtitle" style={{ marginTop: 0, marginBottom: 16 }}>Add Schedule</h2>
        
        {isPremiumOnly && (
          <PlanGateBanner
            title="Premium Monitoring"
            description="Scheduled monitoring is available on Premium plans. Get automated SEO reports delivered to your inbox."
          />
        )}

        <form onSubmit={handleCreate} style={{ opacity: isPremiumOnly ? 0.3 : 1, pointerEvents: isPremiumOnly ? "none" : "auto" }}>
          <div className="input-group" style={{ marginBottom: 12 }}>
            <SimpleUrlInput
              value={url}
              onChange={setUrl}
              required
              placeholder="your-website.com"
              aria-label="Website URL for scheduled monitoring"
              style={{ flex: 2 }}
            />
            <input
              className="input"
              type="text"
              placeholder="Label (optional)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              style={{ flex: 1 }}
            />
          </div>

          <div className="input-group" style={{ marginBottom: 12 }}>
            <input
              className="input"
              type="url"
              placeholder="Generic Webhook URL (optional)"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              style={{ flex: 1 }}
            />
            <input
              className="input"
              type="url"
              placeholder="Slack Webhook URL (optional)"
              value={slackWebhookUrl}
              onChange={(e) => setSlackWebhookUrl(e.target.value)}
              style={{ flex: 1 }}
            />
          </div>

          <div className="schedule-freq-row">
            {FREQUENCIES.map((f) => (
              <label
                key={f.value}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 16px",
                  border: `1px solid ${frequency === f.value ? "var(--accent)" : "var(--border)"}`,
                  borderRadius: "var(--radius-sm)",
                  background: frequency === f.value ? "var(--accent-subtle)" : "var(--bg-card)",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  userSelect: "none",
                }}
              >
                <input
                  type="radio"
                  name="frequency"
                  value={f.value}
                  checked={frequency === f.value}
                  onChange={() => setFrequency(f.value)}
                  style={{ display: "none" }}
                />
                <div>
                  <div style={{ fontWeight: 600, fontSize: "0.875rem" }}>{f.label}</div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{f.desc}</div>
                </div>
              </label>
            ))}

            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                background: "var(--bg-card)",
              }}
            >
              <input
                type="checkbox"
                checked={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.checked)}
              />
              <span style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>Email me when each run completes</span>
            </label>

            <div className="schedule-submit">
              {isLimitReached && !isPremiumOnly && (
                <span style={{ fontSize: "0.75rem", color: "var(--danger)", fontWeight: 600, display: "block", marginBottom: 6 }}>
                  Limit of {maxSchedules} reached
                </span>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving || !url.trim() || isLimitReached}
              >
                {saving ? "Saving..." : "Add Schedule"}
              </button>
            </div>
          </div>
        </form>
      </div>

      {isAdmin ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="input-group" style={{ marginBottom: 0 }}>
            <input
              className="input"
              type="text"
              placeholder="Filter by email..."
              value={adminEmailFilter}
              onChange={(e) => setAdminEmailFilter(e.target.value)}
            />
            <input
              className="input"
              type="text"
              placeholder="Filter by user ID..."
              value={adminUserIdFilter}
              onChange={(e) => setAdminUserIdFilter(e.target.value)}
            />
            <select
              className="input"
              value={adminStatusFilter}
              onChange={(e) => setAdminStatusFilter(e.target.value)}
              style={{ maxWidth: 180 }}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
            <button type="button" className="btn btn-primary" onClick={applyAdminFilters} disabled={loading}>
              {loading ? "Loading..." : "Apply"}
            </button>
          </div>
        </div>
      ) : null}

      {/* List */}
      {loading ? <div className="loading">Loading schedules...</div> : null}

      {!loading && filteredSchedules.length === 0 ? (
        <div className="empty-state">
          No schedules yet. Add one above to start automated monitoring.
        </div>
      ) : null}

      {!loading && filteredSchedules.length > 0 ? (
        <>
          <DataTable
            headers={[
              { key: "url", label: "URL", render: (s) => (
                <div style={{ opacity: s.enabled ? 1 : 0.55 }}>
                  <div style={{ fontWeight: 600, fontSize: "0.875rem" }}>
                    {s.label || s.url.replace(/^https?:\/\/(www\.)?/, "").slice(0, 50)}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", wordBreak: "break-all" }}>
                    {s.url}
                  </div>
                </div>
              ) },
              ...(isAdmin ? [{ key: "user", label: "User", render: (s) => (
                <div style={{ opacity: s.enabled ? 1 : 0.55 }}>
                  <div style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                    {s.user_email || "—"}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                    {s.user_userid || "—"}
                  </div>
                </div>
              ) }] : []),
              { key: "frequency", label: "Frequency", render: (s) => (
                <span style={{ opacity: s.enabled ? 1 : 0.55, whiteSpace: "nowrap" }}>
                  {freqLabel(s.frequency)}
                </span>
              ) },
              { key: "alerts", label: "Alerts", render: (s) => {
                const alerts = [];
                if (s.notify_email || s.notifyEmail) alerts.push("Email");
                if (s.webhook_url || s.webhookUrl) alerts.push("Webhook");
                if (s.slack_webhook_url || s.slackWebhookUrl) alerts.push("Slack");
                return (
                  <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", opacity: s.enabled ? 1 : 0.55 }}>
                    {alerts.join(", ") || "None"}
                  </span>
                );
              } },
              { key: "last_score", label: "Last Score", render: (s) => (
                <div style={{ opacity: s.enabled ? 1 : 0.55 }}>
                  {s.last_score != null ? (
                    <span style={{ fontWeight: 700, color: scoreColor(s.last_score) }}>
                      {s.last_score}/100
                    </span>
                  ) : (
                    <span style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>Not run yet</span>
                  )}
                </div>
              ) },
              { key: "next_run", label: "Next Run", render: (s) => (
                <span style={{ opacity: s.enabled ? 1 : 0.55, whiteSpace: "nowrap", fontSize: "0.85rem" }}>
                  {s.enabled ? nextRunLabel(s.next_run_at) : "—"}
                </span>
              ) },
              { key: "status", label: "Status", render: (s) => (
                <button
                  type="button"
                  onClick={() => toggleEnabled(s)}
                  style={{
                    padding: "4px 12px",
                    borderRadius: 12,
                    border: "none",
                    cursor: "pointer",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    background: s.enabled ? "rgba(22,163,74,0.1)" : "rgba(100,116,139,0.1)",
                    color: s.enabled ? "var(--success)" : "var(--text-muted)",
                    fontFamily: "inherit",
                  }}
                >
                  {s.enabled ? "Active" : "Paused"}
                </button>
              ) },
              { key: "actions", label: "", render: (s) => (
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ borderColor: "var(--border)" }}
                    onClick={() => setEditingSchedule(s)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ color: "var(--danger)", borderColor: "rgba(220,38,38,0.2)" }}
                    onClick={() => handleDelete(s.id)}
                  >
                    Remove
                  </button>
                </div>
              ) }
            ]}
            data={filteredSchedules}
            keyExtractor={(s) => s.id}
          />
          {isAdmin ? (
            <div className="pagination" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={adminPage <= 1 || loading}
                onClick={() => setAdminPage((p) => Math.max(1, p - 1))}
              >
                {"<- Prev"}
              </button>
              <span className="pagination-info">
                Page {adminPagination.page} of {adminPagination.totalPages} — {adminPagination.total} total
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={adminPage >= adminPagination.totalPages || loading}
                onClick={() => setAdminPage((p) => p + 1)}
              >
                {"Next ->"}
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      <div className="hint" style={{ marginTop: 24 }}>
        Scheduled audits run server-side — results appear in your Audit History automatically even when you're not logged in.
        Scores from previous runs are compared so you can track trends in your Dashboard.
      </div>

      {editingSchedule && (
        <EditScheduleModal
          schedule={editingSchedule}
          onClose={() => setEditingSchedule(null)}
          onSuccess={(updated) => {
            setSchedules((prev) =>
              prev.map((s) => (s.id === updated.id ? updated : s))
            );
          }}
        />
      )}
    </div>
  );
}